import {
  DerivableAttribute,
  IdentifiableAttribute,
  ImmutableAttribute,
  MutableAttribute,
} from './Attribute';
import { EntityAttributes } from './EntityAttributes';

export type Updater<Type> = (prevValue: Type) => Type;

export type AttributeStoreGetter<Id> = <Type>(attribute: IdentifiableAttribute<Type, Id>) => Type;
export type AttributeStoreDerive<Type, Id> = (get: AttributeStoreGetter<Id>) => Type;

export abstract class AttributeStore<Id> {
  /**
   * Get value of an attribute from store by its id.
   * Note this usage does **not** trigger re-render of React component.
   * It should be used out of React.
   * For usage within React, use {@link Attribute.useValue}.
   * @param attributeId - The {@link MutableAttribute._id} of attribute to read value from.
   * @example
   * const userName = attributeStore.get(user.attributes.name._id);
   */
  abstract get<Type>(attributeId: Id): Type;

  /**
   * Save value of an attribute to store.
   * It should only be called within {@link BaseEntity}.
   * @param attributeId - The {@link MutableAttribute._id} of attribute to set value.
   * @example
   * attributeStore.set(user.attributes.name._id, 'John Doe');
   */
  abstract set<Type>(attributeId: Id, nextValueOrUpdater: Type | Updater<Type>): void;

  /**
   * Get value of a mutable attribute from store and subscribe to its changes.
   * Note this is a React hook. It can only be used within a React component, or another React hook.
   * For usage out of React, use {@link get}.
   * @param attributeId - {@link MutableAttribute._id} of attribute to read value from.
   * @example
   * const UserView = (props: { user: User }) => { // user is an entity
   *   const userName = useAttribute(props.user.attributes.name);
   * }
   */
  abstract useAttributeValue<Type>(attributeId: Id): Type;

  /**
   * Factory function to create an mutable attribute's id.
   * When this is called, the attribute store should:
   *   1. save the initial value to its inner storage
   *   2. create an id the value as a reference for later use
   * @param initialValue
   */
  abstract mutableId<Type>(initialValue: Type): Id;

  abstract derivableId<Type>(derive: AttributeStoreDerive<Type, Id>): Id;

  /**
   * Factory function to create {@link MutableAttribute}.
   * @param initialValue - initial value of the attribute
   */
  mutable<Type>(initialValue: Type): MutableAttribute<Type, Id> {
    return new MutableAttribute({ store: this, rawValue: initialValue });
  }

  /**
   * Factory function to create {@link ImmutableAttribute}.
   * @param initialValue - initial value of the attribute
   */
  immutable<Type>(initialValue: Type): ImmutableAttribute<Type> {
    return new ImmutableAttribute({ rawValue: initialValue });
  }

  /**
   * Factory function to create {@link DerivableAttribute}.
   * @param derive - function to derive the value of the attribute
   */
  derivable<Type>(derive: AttributeStoreDerive<Type, Id>): DerivableAttribute<Type, Id> {
    return new DerivableAttribute({ store: this, derive });
  }

  /**
   * Save value of an attribute to store.
   * This is a convenience method to create {@link BaseEntity._setValue}.
   * It should only be used within {@link BaseEntity} or its subclasses.
   * @param attributeId - The {@link MutableAttribute._id} of attribute to set value.
   * @example
   * attributeStore.set(user.attributes.name, 'John Doe');
   */
  _setValue = <Type>(
    attribute: MutableAttribute<Type>,
    nextValueOrUpdater: Type | ((prevValue: Type) => Type),
  ) => {
    this.set(attribute._id as Id, nextValueOrUpdater);
  };

  /**
   * Factory function to create {@link EntityAttributes}.
   * @param rawData - initial values of attributes
   * @param mutableAttributes - list of keys of mutable attributes
   * @example
   * interface UserDto {
   *   id: number;
   *   name: string;
   *   email: string;
   * }
   *
   * class User {
   *   readonly attributes = attributeStore.entityAttributes({
   *       id: 1,
   *       name: 'John',
   *       email: 'john@example.com',
   *    },
   *    ['name', 'email'] as const,
   *   );
   *   // It is equivalent to:
   *   readonly attributes = {
   *     id: attributeStore.immutable(1),
   *     name: attributeStore.mutable('John'),
   *     email: attributeStore.mutable('john@example.com'),
   *   };
   * }
   */
  entityAttributes = <Dto extends object, MutableAttributes extends keyof Dto>(
    rawData: Dto,
    mutableAttributes: readonly MutableAttributes[],
  ) => {
    const obj = {} as Record<string, unknown>;
    for (const key of Object.keys(rawData)) {
      if (mutableAttributes.includes(key as MutableAttributes)) {
        obj[key] = this.mutable(rawData[key as MutableAttributes]);
      } else {
        obj[key] = this.immutable(rawData[key as keyof Omit<Dto, MutableAttributes>]);
      }
    }
    return obj as EntityAttributes<Dto, MutableAttributes>;
  };
}
